import React, {
  createContext, useContext, useEffect, useState, useCallback, useMemo,
} from 'react';
import { useClient } from 'urql';
import { PAGE_CONTENTS_QUERY } from 'graphql/pageContent';

interface LocalizedPageContent {
  pageKey: string;
  fi: string;
  sv: string;
  en: string;
}

interface PageContentState {
  loading: boolean;
  error: string | null;
  content: Record<string, LocalizedPageContent>;
}

interface PageContentContextValue extends PageContentState {
  getContent: (pageKey: string, locale: string) => string;
  refresh: () => void;
}

const SUPPORTED_LOCALES: ReadonlySet<string> = new Set(['fi', 'sv', 'en']);

const PageContentContext = createContext<PageContentContextValue>({
  loading: true,
  error: null,
  content: {},
  getContent: () => '',
  refresh: () => {},
});

export const usePageContent = () => useContext(PageContentContext);

interface PageContentProviderProps {
  children: React.ReactNode;
}

export const PageContentProvider: React.FC<PageContentProviderProps> = ({ children }) => {
  const client = useClient();
  const [state, setState] = useState<PageContentState>({
    loading: true,
    error: null,
    content: {},
  });

  const fetchContent = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    client.query(PAGE_CONTENTS_QUERY, {}, { requestPolicy: 'network-only' }).toPromise()
      .then((result) => {
        if (result.error) {
          setState({ loading: false, error: result.error.message, content: {} });
          return;
        }

        const contentMap: Record<string, LocalizedPageContent> = {};
        const items: LocalizedPageContent[] = result.data?.pageContents || [];
        items.forEach((item) => {
          contentMap[item.pageKey] = item;
        });

        setState({ loading: false, error: null, content: contentMap });
      })
      .catch((err) => {
        setState({ loading: false, error: err?.message || 'Unknown error', content: {} });
      });
  }, [client]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const getContent = useCallback((pageKey: string, locale: string): string => {
    const page = state.content[pageKey];
    if (!page) return '';
    const resolvedLocale = SUPPORTED_LOCALES.has(locale) ? locale : 'fi';
    return page[resolvedLocale as keyof LocalizedPageContent] || '';
  }, [state.content]);

  const value = useMemo(() => ({
    ...state,
    getContent,
    refresh: fetchContent,
  }), [state, getContent, fetchContent]);

  return (
    <PageContentContext.Provider value={value}>
      {children}
    </PageContentContext.Provider>
  );
};

export default PageContentContext;
